/* eslint-disable @typescript-eslint/no-explicit-any */
import { createAction } from 'redux-actions';

import { CalendarItem, CalendarItemMeta } from 'utils/calendarItems';
import { CalendarSelectionMode } from 'constants/CalendarState';
import { Filter } from 'state/calendarFilter';
import { LocalDate } from 'js-joda';
import { SelectedInventoryItemId } from 'state/calendar';
import { SelectedTimelineItem } from 'types/tableBasedInventoryRangeVariants';
import { TicketGroupsByDateTime } from 'selectors/ticketGroupsByDateTimeSelector';
import { core } from '@tock/coretypes';
import { dashboard } from 'types';
import { filter, forEach } from 'es-toolkit/compat';
import { getSelectedInventoryItemId } from 'utils/calendarUtils';
import AppConstants from 'constants/AppConstants';
import protos from 'utils/protos';

const {
  FilterCalendarRequest,
  PurchaseRescheduleRequest,
  RescheduleRequest,
  TicketGroupPriceChange,
} = protos;

const editedStatusMapping: { [status: string]: boolean | null } = {
  ALL: null,
  YES: false,
  NO: true,
};

export interface SquareCoordinates {
  openPositionX: number;
  openPositionY: number;
}

interface CalendarRequest extends Partial<Filter> {
  serviceType?: string[];
  numDays: number;
  startDate: string;
}

export type CalendarPopoverTypes = 'CALENDAR_ITEM_POPOVER';

export interface CalendarPopoverOpenPayload {
  popoverType: CalendarPopoverTypes;
  coordinates?: SquareCoordinates;
}

export enum CalendarPopoverActionTypes {
  CALENDAR_POPOVER_OPEN = 'CALENDAR_POPOVER_OPEN',
  CALENDAR_POPOVER_CLOSE = 'CALENDAR_POPOVER_CLOSE',
}

export interface CalendarPopoverOpenAction {
  type: CalendarPopoverActionTypes.CALENDAR_POPOVER_OPEN;
  payload: CalendarPopoverOpenPayload;
}

export interface CalendarPopoverCloseAction {
  type: CalendarPopoverActionTypes.CALENDAR_POPOVER_CLOSE;
}

const massageRequest = (request: CalendarRequest) => {
  const { editedStatus, serviceType, guestTag, visitTag, ...rest } = request;
  return {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    isPristine: editedStatusMapping[editedStatus!],
    serviceType: filter(serviceType),
    visitTag,
    guestTag,
    ...rest,
  };
};

export function clearCalendarDataFromStore(): BaseTockAction {
  return {
    type: AppConstants.CALENDAR_CLEAR_DATA_FROM_STORE,
  };
}

export function fetchDailySnapshot(date: LocalDate): ApiAction {
  const calendarRequest: CalendarRequest = {
    numDays: 1,
    startDate: date.toString(),
  };
  const request = massageRequest(calendarRequest);
  return {
    TockApi: {
      postListProto: '/admin/calendar/summary',
      type: AppConstants.MONTH_CALENDAR_FETCH_ALL,
      request: FilterCalendarRequest.fromObject(request),
      discardOldRequests: true,
    },
  };
}

export function fetchMonthCalendar(request: CalendarRequest): ApiAction {
  const actualRequest = massageRequest(request);

  return {
    TockApi: {
      postListProto: '/admin/calendar/summary',
      type: AppConstants.MONTH_CALENDAR_FETCH_ALL,
      request: FilterCalendarRequest.fromObject(actualRequest),
      discardOldRequests: true,
    },
  };
}

export function setLoading(loading: boolean): BaseTockAction {
  return {
    type: AppConstants.CALENDAR_SET_LOADING,
    payload: loading,
  };
}

export function calendarUpdate(
  businessId: number,
  changeUpdate: dashboard.CalendarDayEntityUpdates
): BaseTockAction {
  return {
    type: AppConstants.CALENDAR_ENTITY_UPDATE,
    payload: changeUpdate,
    context: businessId,
  };
}

export function calendarUpdateSave(businessId: number, date: string): BaseTockAction {
  return {
    type: AppConstants.CALENDAR_ENTITY_SAVE_DATE,
    payload: date,
    context: businessId,
  };
}

export function calendarUpdateRestore(businessId: number, date: string): BaseTockAction {
  return {
    type: AppConstants.CALENDAR_ENTITY_RESTORE_DATE,
    payload: date,
    context: businessId,
  };
}

export function monthCalendarUpdate(
  changeUpdate: dashboard.CalendarMonthEntityUpdates
): BaseTockAction {
  return {
    type: AppConstants.MONTH_CALENDAR_ENTITY_UPDATE,
    payload: changeUpdate,
  };
}

export function rescheduleTimeFlexible(
  purchase: core.PastPurchase,
  newTicketGroup: core.TicketGroup,
  dateTime: string,
  emailPolicy: core.EmailPolicy,
  sourceBehavior: core.RescheduleRequest.SourceGroupBehaviorType,
  requestedTableId?: number[]
): ApiAction {
  return {
    TockApi: {
      putProto: '/admin/purchase/reschedule/flex',
      request: RescheduleRequest.fromObject({
        purchaseId: purchase.id,
        targetDateTime: dateTime,
        ticketGroupIdHint: newTicketGroup.id,
        emailPolicy,
        sourceBehavior,
        requestedTableId,
      }),
      type: AppConstants.CALENDAR_ACTION,
      toast: 'Reservations rescheduled',
    },
  };
}

export function reschedulePurchaseOntoExisting(
  purchase: core.PastPurchase,
  originalTicketGroup: core.TicketGroup,
  newTicketGroup: core.TicketGroup,
  dateTime: string,
  modificationType: core.PurchaseRescheduleRequest.ModificationType,
  emailPolicy: core.EmailPolicy,
  makeSourceAvailable: boolean
): ApiAction {
  return {
    TockApi: {
      putProto: '/admin/purchase/reschedule',
      request: PurchaseRescheduleRequest.fromObject({
        purchaseId: purchase.id,
        sourceTicketGroupId: originalTicketGroup.id,
        targetDateTime: dateTime,
        targetTicketGroupId: newTicketGroup.id,
        modificationType,
        emailPolicy,
        makeSourceAvailable,
      }),
      type: AppConstants.CALENDAR_ACTION,
      toast: 'Reservations rescheduled',
    },
  };
}

export function reschedulePurchaseOntoNew(
  purchase: core.PastPurchase,
  originalTicketGroup: core.TicketGroup,
  dateTime: string,
  modificationType: core.PurchaseRescheduleRequest.ModificationType,
  emailPolicy: core.EmailPolicy,
  makeSourceAvailable: boolean
): ApiAction {
  return {
    TockApi: {
      putProto: '/admin/purchase/reschedule',
      request: PurchaseRescheduleRequest.fromObject({
        purchaseId: purchase.id,
        sourceTicketGroupId: originalTicketGroup.id,
        targetDateTime: dateTime,
        modificationType,
        emailPolicy,
        makeSourceAvailable,
      }),
      type: AppConstants.CALENDAR_ACTION,
      toast: 'Reservations rescheduled',
    },
  };
}

export function setDate(date: any): BaseTockAction {
  return {
    type: AppConstants.CALENDAR_SET_DATE,
    payload: date,
  };
}

export function setTextFilter(filter: string): BaseTockAction {
  return {
    type: AppConstants.CALENDAR_SET_TEXT_FILTER,
    payload: filter,
  };
}

export function selectTab(tab: any): BaseTockAction {
  return {
    type: AppConstants.CALENDAR_SELECT_TAB,
    payload: tab,
  };
}

export function setBookingModalVisible(show: boolean): BaseTockAction {
  return {
    type: AppConstants.CALENDAR_SET_BOOKING_MODAL_VISIBLE,
    payload: show,
  };
}

export function setShowTicketSubsetHoldModal(showTicketSubsetHoldModal: boolean): BaseTockAction {
  return {
    type: AppConstants.CALENDAR_SET_SHOW_TICKET_SUBSET_HOLD_MODAL,
    payload: showTicketSubsetHoldModal,
  };
}

export function clearSelectedCalendarItem(): BaseTockAction {
  return {
    type: AppConstants.CALENDAR_CLEAR_SELECTED_CALENDAR_ITEM,
  };
}

export function specifySelectedCalendarItem(
  selectedInventoryItemId: SelectedInventoryItemId
): BaseTockAction {
  return {
    type: AppConstants.CALENDAR_SPECIFY_SELECTED_CALENDAR_ITEM,
    payload: selectedInventoryItemId,
  };
}

export function setSelectedTimelineItem(timelineItem: SelectedTimelineItem): BaseTockAction {
  return {
    type: AppConstants.CALENDAR_SELECT_TABLE_STATE_RANGE,
    payload: timelineItem,
  };
}

export function clearSelectedTableStateRange(): BaseTockAction {
  return {
    type: AppConstants.CALENDAR_CLEAR_SELECTED_TABLE_STATE_RANGE,
  };
}

interface FetchSetSelectedCalendarItemOptions {
  type: 'Fetch';
  calendarItemId: SelectedInventoryItemId | undefined;
  ticketGroup: core.TicketGroup;
}

interface RealtimeSetSelectedCalendarItemOptions {
  type: 'Realtime';
  calendarItemId: SelectedInventoryItemId | undefined;
}

export type SetSelectedCalendarItemOptions =
  | FetchSetSelectedCalendarItemOptions
  | RealtimeSetSelectedCalendarItemOptions;

export function setSelectedCalendarItem(calendarItem: CalendarItem): ApiAction | BaseTockAction {
  const calendarItemId = getSelectedInventoryItemId(calendarItem);
  if (!calendarItemId) {
    return clearSelectedCalendarItem();
  }

  return specifySelectedCalendarItem(calendarItemId);
}

export function addSelectedCalendarItem(calendarItem: CalendarItem): ApiAction | BaseTockAction {
  return {
    type: AppConstants.CALENDAR_ITEM_ADD_SELECTION,
    payload: calendarItem,
  };
}

export function removeSelectedCalendarItem(calendarItem: CalendarItem): ApiAction | BaseTockAction {
  return {
    type: AppConstants.CALENDAR_ITEM_REMOVE_SELECTION,
    payload: calendarItem,
  };
}

/**
 * when you select a calendar item, if there's a table id this is triggered.
 */

export const setSelectedTableIdCalendarSquare = createAction(
  AppConstants.CALENDAR_SET_SELECTED_TABLE_ID_CALENDAR_SQUARE
);

export function setSelectedCalendarItemMeta(meta: CalendarItemMeta) {
  return {
    type: AppConstants.CALENDAR_ITEM_SET_META,
    payload: meta,
  };
}

export function clearSelectedCalendarItemMeta() {
  return {
    type: AppConstants.CALENDAR_ITEM_CLEAR_META,
  };
}

// Sets the selected table ids for a book now / book via email
export function setSelectedTablesToBook(tableIdsToBook: number[]) {
  return {
    type: AppConstants.SET_SELECTED_TABLE_IDS_TO_BOOK,
    payload: tableIdsToBook,
  };
}

// Clears the selected table ids for a book now / book via email
export const clearSelectedTablesToBook = createAction(
  AppConstants.CLEAR_SELECTED_TABLE_IDS_TO_BOOK
);

/**
 * selects all calendar items
 */
export function selectAllCalendarItems(ticketGroupsByDateTime: TicketGroupsByDateTime) {
  return {
    type: AppConstants.CALENDAR_SELECT_ALL_ITEMS,
    payload: ticketGroupsByDateTime,
  };
}
/**
 * deselects all calendar items
 */
export const deselectAllCalendarItems = createAction(AppConstants.CALENDAR_DESELECT_ALL_ITEMS);
/**
 * sets the type of selection to do
 */
export function setSelectionMode(mode?: CalendarSelectionMode): BaseTockAction {
  return {
    type: AppConstants.CALENDAR_SET_SELECTION_MODE,
    payload: mode,
  };
}

/**
 * for flex there is a "sub" reschedule mode
 */
export const setRescheduleMode = createAction(AppConstants.CALENDAR_SET_RESCHEDULE_MODE);

/**
 * sets the table id that was clicked on
 */
export const setSelectedTableId = createAction(AppConstants.CALENDAR_SET_SELECTED_TABLE_ID);

/**
 * when in price edit, you want to also select items of the same kind
 */
export function addRelatedCalendarItems(
  calendarItem: CalendarItem,
  ticketGroupsByDateTime: TicketGroupsByDateTime
): BaseTockAction {
  return {
    type: AppConstants.CALENDAR_ADD_RELATED_ITEMS,
    payload: {
      calendarItem,
      ticketGroupsByDateTime,
    },
  };
}

/**
 * when in price edit, you want to also remove items of the same kind
 */
export function removeRelatedCalendarItems(
  calendarItem: CalendarItem,
  ticketGroupsByDateTime: TicketGroupsByDateTime
): BaseTockAction {
  return {
    type: AppConstants.CALENDAR_REMOVE_RELATED_ITEMS,
    payload: {
      calendarItem,
      ticketGroupsByDateTime,
    },
  };
}

export function setCalendarState(state: string): BaseTockAction {
  return {
    type: AppConstants.CALENDAR_SET_STATE,
    payload: state,
  };
}

export function updateTicketGroupPrices(uniqueRequests: core.TicketGroupPriceChange[]): ApiAction {
  const requests: any[] = [];
  forEach(uniqueRequests as any[], (request) => {
    requests.push({
      path: '/ticket/group/price',
      method: 'PUT',
      payload: TicketGroupPriceChange.fromObject(request),
      requestType: TicketGroupPriceChange,
    });
  });

  return {
    TockApi: {
      batchProto: requests,
      type: AppConstants.CALENDAR_ACTION,
      toast: 'Ticket prices updated',
    },
  };
}

export function setMovingTable(
  calendarItemToMove: CalendarItem | undefined,
  dateTimeToMove?: string
): BaseTockAction {
  return {
    type: AppConstants.CALENDAR_SET_MOVING_TABLE,
    payload: {
      calendarItemToMove,
      dateTimeToMove,
    },
  };
}

export function setCalendarItemToHold(calendarItemToHold: CalendarItem): BaseTockAction {
  return {
    type: AppConstants.CALENDAR_SET_CALENDAR_ITEM_TO_HOLD,
    payload: calendarItemToHold,
  };
}

export function setShowModifyActionsModal(showActionsModal: boolean): BaseTockAction {
  return {
    type: AppConstants.CALENDAR_SET_SHOW_ACTIONS_MODAL,
    payload: showActionsModal,
  };
}

export function openCalendarPopover(
  popoverType: CalendarPopoverTypes,
  coordinates?: SquareCoordinates
): CalendarPopoverOpenAction {
  return {
    type: CalendarPopoverActionTypes.CALENDAR_POPOVER_OPEN,
    payload: { popoverType, coordinates },
  };
}

export function closeCalendarPopover(): CalendarPopoverCloseAction {
  return {
    type: CalendarPopoverActionTypes.CALENDAR_POPOVER_CLOSE,
  };
}

export interface LoadHistoryRequest {
  maxServerId: number;
  serviceDate: string;
}

export function fetchServiceHistory(request: LoadHistoryRequest): ApiAction {
  const requestUrl =
    `/activity?lessThanId=${request.maxServerId}` + `&limit=50&serviceDate=${request.serviceDate}`;

  return {
    TockApi: {
      getListProto: requestUrl,
      type: AppConstants.NULL,
    },
  };
}

export const setFocusedTimeSpan = (startTime: string, duration: number) => {
  return {
    type: AppConstants.CALENDAR_SET_FOCUSED_TIME_SPAN,
    payload: { startTime, duration },
  };
};

export const resetFocusedTimeSpan = () => {
  return {
    type: AppConstants.CALENDAR_RESET_FOCUSED_TIME_SPAN,
  };
};
